import { useCallback } from 'react';
import { sdk } from '@/sdk';
import { useSWRConfig } from 'swr';
import { Wishlist } from '@shared/types/wishlist/Wishlist';
import useCustomRouter from '@/hooks/useCustomRouter';
import { DashboardLinks } from '@/components/pages/dashboard/constants';
import { RemoveLineItemsResponse } from '@/lib/utils/removeLineItemsResult';

// EWM-690 W4 — purchase-list mutation callbacks, fetch-free (no queryWishlists
// observer). `usePurchaseLists` composes these; consumers that only mutate
// (cart-client-wrapper, usePurchaseList) use this hook directly and mount no
// limit=500 wishlist observer.
//
// The list-observer variants below revalidate via `mutateAll` (pattern-match on
// the '/action/wishlist/' key) rather than a bound observer mutate: a customer
// has one immutable store, so every wishlist-list key is the same one key.
const usePurchaseListActions = () => {
  const router = useCustomRouter();

  const { mutate: globalMutate } = useSWRConfig();

  const mutateAll = useCallback(() => {
    globalMutate((key) => typeof key === 'object' && key?.[0] === '/action/wishlist/');
  }, [globalMutate]);

  const createPurchaseList = useCallback(
    async ({ name, description, store }: Wishlist) => {
      const res = await sdk.composableCommerce.wishlist.createWishlist(
        { name, description },
        { storeKey: store?.key ?? '' },
      );

      if (!res.isError) {
        // Force a revalidation of the wishlist list cache. SWR config
        // disables `revalidateIfStale` + `revalidateOnFocus`, so we need
        // to ask explicitly. Awaited so the list page renders fresh data
        // on the form's router.back() — previously fire-and-forget left
        // it showing stale state.
        await globalMutate((key) => typeof key === 'object' && key?.[0] === '/action/wishlist/', undefined, {
          revalidate: true,
        });
      }

      return res.isError ? null : res.data;
    },
    [globalMutate],
  );

  const updatePurchaseList = useCallback(
    async ({ wishlistId, name, description }: Partial<Wishlist>) => {
      const res = await sdk.composableCommerce.wishlist.updateWishlist({ name, description }, { id: wishlistId });

      const { wishlistId: itemWishlistId } = res.isError ? {} : res.data;

      mutateAll();
      globalMutate(['/action/wishlist/getWishlist', itemWishlistId]);

      router.push(DashboardLinks.shoppingListDetail(itemWishlistId as string));

      return res.isError ? null : res.data;
    },
    [mutateAll, globalMutate, router],
  );

  const deletePurchaseList = useCallback(
    async ({ wishlistId, store }: Partial<Wishlist>) => {
      const res = await sdk.callAction({
        actionName: `wishlist/deleteWishlist?id=${wishlistId}&storeKey=${store?.key ?? ''}`,
      });

      mutateAll();

      return res.isError ? null : {};
    },
    [mutateAll],
  );

  const addToWishlists = useCallback(
    async ({ wishlistIds, sku, count }: { wishlistIds: string[]; sku: string; count: number }) => {
      const result = await sdk.callAction<Wishlist[]>({
        actionName: 'wishlist/addToWishlists',
        payload: {
          wishlistIds,
          variant: { sku },
          count,
        },
      });

      mutateAll();
      wishlistIds.forEach((wishlistId) => globalMutate(['/action/wishlist/getWishlist', wishlistId]));
      router.refresh();

      // EWM-188: signal success/failure so callers can branch their
      // confirmation toast (mirrors useCart's `addItem`). The action
      // swallows the raw error on failure but reports `success: false`.
      if (!result.isError) {
        return { success: true, data: result?.data };
      }

      return { success: false };
    },
    [mutateAll, globalMutate, router],
  );

  const removeFromWishlists = useCallback(
    async (
      wishlists: {
        lineItemId: string;
        wishlistId: string;
      }[],
    ) => {
      // EWM-892 — the action answers with the bare `Wishlist[]` when every list
      // committed, or `{ updated, failed }` when only some did. Callers run the
      // payload through `toRemoveLineItemsResult` rather than assuming an array.
      const result = await sdk.callAction<RemoveLineItemsResponse>({
        actionName: 'wishlist/removeLineItems',
        payload: { wishlists },
      });

      mutateAll();
      wishlists.forEach(({ wishlistId }) => globalMutate(['/action/wishlist/getWishlist', wishlistId]));
      router.refresh();

      if (!result.isError) {
        return { data: result?.data };
      }

      return {};
    },
    [mutateAll, globalMutate, router],
  );

  const setDefaultPurchaseList = useCallback(
    async (wishlistId: string | null): Promise<boolean> => {
      const res = await sdk.callAction({
        actionName: 'wishlist/setDefaultPurchaseList',
        payload: { wishlistId },
      });

      if (!res.isError) {
        globalMutate('/action/account/getAccount');
      }

      return !res.isError;
    },
    [globalMutate],
  );

  return {
    mutateAll,
    createPurchaseList,
    updatePurchaseList,
    deletePurchaseList,
    addToWishlists,
    removeFromWishlists,
    setDefaultPurchaseList,
  };
};

export default usePurchaseListActions;
